/**
 * PasswordInput.jsx
 * Input de contraseña con:
 *   - Botón toggle para mostrar/ocultar texto
 *   - Barra de fortaleza opcional (showStrength)
 *
 * Props:
 *   id             string — id del input (para label htmlFor)
 *   error          boolean — si hay error de validación
 *   showStrength   boolean — mostrar barra de fortaleza
 *   strengthValue  objeto { level, label } de getPasswordStrength()
 *   ...rest        pasan a <input> (register de RHF, placeholder, etc.)
 */
import { useState } from 'react';
import f from '../../styles/forms.module.css';

const EyeOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeClosed = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// Mapa de clases por nivel de fortaleza
const STRENGTH_CLASSES = {
  weak:   { fill: f.strengthWeak,   text: f.strengthTextWeak   },
  medium: { fill: f.strengthMedium, text: f.strengthTextMedium },
  strong: { fill: f.strengthStrong, text: f.strengthTextStrong },
};

const PasswordInput = ({
  id,
  error = false,
  showStrength = false,
  strengthValue = null,
  ...rest
}) => {
  const [visible, setVisible] = useState(false);

  const strengthClasses = strengthValue ? STRENGTH_CLASSES[strengthValue.level] : null;

  return (
    <div>
      <div className={f.inputWrapper}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className={`${f.input} ${error ? f.inputError : ''}`}
          aria-invalid={error ? 'true' : undefined}
          {...rest}
        />
        <button
          type="button"
          className={f.eyeButton}
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          tabIndex={-1}
        >
          {visible ? <EyeClosed /> : <EyeOpen />}
        </button>
      </div>

      {showStrength && strengthValue && (
        <>
          <div className={f.strengthBar} aria-hidden="true">
            <div className={`${f.strengthFill} ${strengthClasses.fill}`} />
          </div>
          <span className={`${f.strengthText} ${strengthClasses.text}`}>
            Fortaleza: {strengthValue.label}
          </span>
        </>
      )}
    </div>
  );
};

export default PasswordInput;
